import { useCallback, useEffect, useState, type JSX } from 'react';
import { usePendingAppAction } from '@taki/ui';
import type { AppTab } from './data.js';
import { CatsListScreen } from './CatsListScreen.js';
import { CatDetailScreen } from './CatDetailScreen.js';
import { ReservationsScreen } from './ReservationsScreen.js';

/**
 * Ecran racine de l'app Cat Manager Six.
 *
 * Aucun ScreenLayout ici : chaque sous-ecran porte le sien. La racine ne fait
 * que gerer la navigation interne (onglet actif + chat selectionne) via useState,
 * sans router et sans acces aux donnees.
 *
 *  - onglet "Les chats" : liste -> clic sur un chat -> fiche (avec retour) ;
 *  - onglet "Les visites" : liste des reservations (bascule aujourd'hui / semaine).
 *
 * C'est aussi elle qui consomme les actions du dock (usePendingAppAction) :
 * elle pilote l'onglet actif, donc elle se place sur le bon onglet puis relaie
 * la demande d'ouverture du Modal au sous-ecran via props (openFormRequested /
 * onOpenFormHandled), le sous-ecran acquitte une fois le Modal ouvert.
 */

/** Formulaire dont l'ouverture est demandee par une action du dock. */
type FormRequest = 'cat' | 'visit';

export function CatManagerSixScreen(): JSX.Element {
  const [tab, setTab] = useState<AppTab>('cats');
  const [selectedCatId, setSelectedCatId] = useState<string | null>(null);
  const [formRequest, setFormRequest] = useState<FormRequest | null>(null);

  const onTabChange = useCallback((next: AppTab) => {
    setTab(next);
    setSelectedCatId(null); // changer d'onglet reinitialise la selection
  }, []);

  /** Acquittement du sous-ecran : la demande d'ouverture est traitee. */
  const clearFormRequest = useCallback(() => {
    setFormRequest(null);
  }, []);

  // --- Actions du dock (shortcuts) -----------------------------------------
  // Le shell pose l'action (taki.app.json, champ `actions`) puis navigue ici ;
  // la racine bascule sur l'onglet de l'action, pose la demande d'ouverture du
  // Modal pour le sous-ecran, puis consomme. `actionId` repasse a null apres
  // consume() : l'effet ne rejoue pas, pas de double ouverture.
  const { actionId, consume } = usePendingAppAction('cat-manager-six');
  useEffect(() => {
    if (actionId == null) {
      return;
    }
    if (actionId === 'add-cat') {
      setTab('cats');
      setSelectedCatId(null); // quitte une eventuelle fiche : le Modal vit sur la liste
      setFormRequest('cat');
    } else if (actionId === 'add-visit') {
      setTab('visits');
      setSelectedCatId(null);
      setFormRequest('visit');
    }
    consume();
  }, [actionId, consume]);

  if (tab === 'visits') {
    return (
      <ReservationsScreen
        activeTab={tab}
        onTabChange={onTabChange}
        openFormRequested={formRequest === 'visit'}
        onOpenFormHandled={clearFormRequest}
      />
    );
  }

  if (selectedCatId === null) {
    return (
      <CatsListScreen
        activeTab={tab}
        onTabChange={onTabChange}
        onSelectCat={setSelectedCatId}
        openFormRequested={formRequest === 'cat'}
        onOpenFormHandled={clearFormRequest}
      />
    );
  }

  return (
    <CatDetailScreen
      activeTab={tab}
      onTabChange={onTabChange}
      catId={selectedCatId}
      onBack={() => setSelectedCatId(null)}
    />
  );
}

export default CatManagerSixScreen;
